import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import AgentReport from './AgentReport';
import SensorCountReport from './SensorCountReport';
import CustomerSupportExport from './CustomerSupportExport';
import CreateWatchDogReport from './CreateWatchDogReport';
import { Splitter } from '@progress/kendo-react-layout';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import NoSelectedSensorsScreen from '../../../components/NoSelectedSensorsScreen';
import clsx from 'clsx';
import * as BasAgentActions from '../../../actions/basAgentActions';
import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as classes from './CreateReport.module.css';
import * as aClasses from './InspectionReportCreate.module.css';
import ReportConfigurationCreate from './ReportConfigurationCreate';
import BillingMetricsReport from './BillingMetricsReport';
import queryString from 'query-string';

const reportTypes = [
    {
        id:          'chooseReport',
        name:        'Select Report Type',
        superUser:   false,
        description: 'Select a report type'
    },
    {
        id:          'basAgent',
        name:        'BAS Agent Report',
        superUser:   true,
        key2actUser: true,
        description: 'Report of BAS Agents for selected company'
    },
    {
        id:          'billingMetrics',
        name:        'Billing Metrics',
        superUser:   true,
        key2actUser: true,
        description: 'Billing metrics report for audit of companies'
    },
    {
        id:          'customerSupportExport',
        name:        'Customer Support Export',
        superUser:   true,
        key2actUser: true,
        description: 'Customer Support Export'
    },
    {
        id:          'inspection',
        name:        'Inspection Report',
        superUser:   false,
        description: 'An Inspection Report uses your existing Rules and configuration to provide a Site-level inspection to communicate Site performance and opportunities for improvement in an easy-to-use PDF format.'
    },
    {
        id:          'optimization',
        name:        'Optimization Report',
        superUser:   false,
        key2actUser: false,
        description: 'Optimization Report'
    },
    {
        id:          'sensorCountReport',
        name:        'Sensor Count Report',
        superUser:   false,
        key2actUser: false,
        description: 'A report containing a count of sensors by Client, Site and Agent'
    },
    {
        id:          'watchdog',
        name:        'Watchdog Report',
        superUser:   false,
        description: 'A Watchdog Report is used to provide a Site-level Equipment report to communicate Equipment performance and opportunities for improvement in an easy-to-use PDF format.'
    }
];

const isKey2ActUser = email => {
    return email.toLowerCase().trimEnd().endsWith('@key2act.com');
};

const CreateReport = ({
    clearBasAgentsReport,
    currentUser,
    fetchBasAgentsReport,
    handleBackClick,
    history,
    setReportName,
    selectedReport
}) => {
    const [reportType, setReportType] = useState('chooseReport');
    const [reportComponent, setReportComponent] = useState(
        <NoSelectedSensorsScreen
            message="Please select a report type"
            height={'65vh'}
        />);
    const [reportDescription, setReportDescription] = useState('Select a report type');
    const [verticalPanes] = useState([{
        size:      '70px',
        resizable: false
    },
    {
        resizable: false
    }
    ]);

    // type field is required for save & Generate report
    const changeUrl = (type) => {
        const { pathname } = history.location;
        const queryValues = queryString.parse(history.location.search);
        queryValues.type = type;

        const queryValuesString = queryString.stringify(queryValues);

        history.push(`${pathname}?${queryValuesString}`);
    };

    const handleReportType = async (event) => {
        setReportType(event.target.value);
        changeUrl(event.target.value);
        
        const report = reportTypes.find(r => r.id === event.target.value);
        if (report) {
            setReportDescription(report.description);
            setReportName(report.name);
        }
        else {
            setReportDescription('');
            setReportName('New Report');
        }

        switch (event.target.value) {
            case 'basAgent':
                setReportComponent(<AgentReport />);
                break;
            case 'sensorCountReport':
                setReportComponent(<SensorCountReport />);
                break;
            case 'inspection':
                setReportComponent(
                    <ReportConfigurationCreate
                        handleBackClick={handleBackClick}
                        reportType={'inspection'}
                    />
                );
                break;
            case 'watchdog':
                setReportComponent(
                    <CreateWatchDogReport handleBackClick={handleBackClick} />);
                break;
            case 'customerSupportExport':
                setReportComponent(<CustomerSupportExport />);
                break;
            case 'optimization':
                setReportComponent(
                    <ReportConfigurationCreate
                        handleBackClick={handleBackClick}
                        reportType={'optimization'}
                    />
                );
                break;
            case 'billingMetrics':
                setReportComponent(
                    <BillingMetricsReport 
                        handleBackClick={handleBackClick}
                    />
                );
                break;
            default: 
                setReportComponent(
                    <NoSelectedSensorsScreen
                        message="Please select a report type"
                        height={'65vh'}
                    />
                );
        }
    };

    const MenuProps = {
        disablePortal:      true,
        getContentAnchorEl: () => null,
        PaperProps:         {
            style: {
                maxHeight: 500
            },
        },
    };

    return (
        <Splitter
            panes={verticalPanes}
            orientation={'vertical'}
        >
            <div className={classes.reportTypeWrapper}>
                <div className={classes.SelectorPane}>
                    <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                        <InputLabel
                            htmlFor="reportType"
                            classes={{
                                error:   aClasses.error,
                                focused: aClasses.labelFocused,
                                root:    aClasses.label
                            }}
                        >
                            Report Type<span className={aClasses.required}> *</span>
                        </InputLabel>
                        <Select
                            value={reportType}
                            onChange={e => handleReportType(e)}
                            className={aClasses.select}
                            MenuProps={MenuProps}
                            inputProps={{
                                name: 'reportType',
                                id:   'reportType',
                            }}
                        >
                            {
                                reportTypes.map(type => {
                                    if (!type.superUser) {
                                        return (
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                                classes={{
                                                    root: aClasses.select
                                                }}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        );
                                    }
                                    else if (((type.superUser && currentUser.isSuperUser) || (type.key2actUser && isKey2ActUser(currentUser.email)))) {
                                        return (
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                                classes={{
                                                    root: aClasses.select
                                                }}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        );
                                    }
                                    return null;
                                })
                            }
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.ReportDescription}>
                    <span className={clsx('material-icons-outlined md-18', classes.HelperIcon)}>
                        info
                    </span>
                    <span style={{display: 'inline-block', paddingLeft: '5px'}}>{reportDescription}</span>

                </div>
            </div>
            <div className={classes.ReportComponent}>
                {
                    reportComponent
                }
            </div>
        </Splitter>
    );
};
const mapStateToProps = state => {
    return {
        basAgentsReport: state.basAgent.basAgentsReport,
        currentUser:     state.currentUser
    };
};

const mapDispatchToProps = ({
    ...BasAgentActions,
    ...BobActions,
    ...DashboardActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateReport));
